import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Settings2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { USER_ROLES } from '@/lib/constants/roles';
import { usePermission } from '@/lib/permissions/use-permission';
import { useDeleteVoiceWebhook, useVoiceWebhookList } from '../api';
import { JsonViewerDialog } from '../components/JsonViewerDialog';
import type { VoiceWebhookRow } from '../types';

interface JsonModal {
  title: string;
  content: string;
}

export function VoiceWebhookListPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const isSAdmin = role === USER_ROLES.S_ADMIN;
  const canAddAcl = usePermission('Manage Campaign.Webhook List', BASE_PERMISSION.ADD);
  const canEditAcl = usePermission('Manage Campaign.Webhook List', BASE_PERMISSION.EDIT);
  const canDeleteAcl = usePermission('Manage Campaign.Webhook List', BASE_PERMISSION.DELETE);
  // Super-admin still can't manage webhooks (unchanged rule). Sub-super-admin
  // is let through only with the matching ACL grant instead of a blanket block.
  const canAdd = !isSAdmin && canAddAcl;
  const canEdit = !isSAdmin && canEditAcl;
  const canDelete = !isSAdmin && canDeleteAcl;
  const query = useVoiceWebhookList();
  const del = useDeleteVoiceWebhook();
  const rows = query.data ?? [];

  const [pendingDelete, setPendingDelete] = useState<VoiceWebhookRow | null>(null);
  const [jsonModal, setJsonModal] = useState<JsonModal | null>(null);

  const hasJsonPayload = (raw: string | undefined) => {
    if (!raw) return false;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0;
    } catch {
      return false;
    }
  };

  const columns = useMemo<ColumnDef<VoiceWebhookRow>[]>(() => {
    const cols: ColumnDef<VoiceWebhookRow>[] = [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      { accessorKey: 'userId', header: 'User ID', size: 100 },
      { accessorKey: 'username', header: 'Username', size: 140 },
      {
        accessorKey: 'webhookName',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/update-webhooks/${row.original.id}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.webhookName}
          </Link>
        ),
      },
      {
        accessorKey: 'url',
        header: 'URL',
        cell: ({ row }) => (
          <span className="break-all font-mono text-xs">{row.original.url}</span>
        ),
      },
      { accessorKey: 'event', header: 'Event', size: 180 },
      {
        id: 'headerView',
        header: 'Headers',
        size: 90,
        enableSorting: false,
        cell: ({ row }) =>
          hasJsonPayload(row.original.headerJson) ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setJsonModal({ title: 'Headers', content: row.original.headerJson ?? '' })
              }
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'bodyView',
        header: 'Body',
        size: 90,
        enableSorting: false,
        cell: ({ row }) =>
          hasJsonPayload(row.original.bodyJson) ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setJsonModal({ title: 'Body', content: row.original.bodyJson ?? '' })
              }
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
    ];

    if (canEdit || canDelete) {
      cols.push({
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{row.original.webhookName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {canEdit && (
                <DropdownMenuItem
                  onClick={() => navigate(`/update-webhooks/${row.original.id}`)}
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => setPendingDelete(row.original)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      });
    }

    return cols;
  }, [canEdit, canDelete, navigate]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    del.mutate(pendingDelete.id, {
      onSuccess: (res) => {
        toast.success(res?.message ?? 'Webhook deleted');
        setPendingDelete(null);
      },
      onError: () => toast.error('Could not delete the webhook.'),
    });
  };

  const canViewList = usePermission('Manage Campaign.Webhook List', BASE_PERMISSION.VIEW);

  if (!canViewList) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="CDR Webhooks"
        description="HTTP callbacks fired on voice campaign lifecycle events."
        actions={
          canAdd && (
            <Button asChild>
              <Link to="/add-webhooks">
                <Plus className="h-4 w-4" />
                Add webhook
              </Link>
            </Button>
          )
        }
      />

      <DataTable<VoiceWebhookRow, unknown>
        tableId="webhooks:voice:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search webhooks…"
        emptyTitle="No webhooks yet"
        emptyDescription="Add a webhook to receive HTTP callbacks for voice events."
        getRowId={(row) => String(row.id)}
        initialColumnPinning={canEdit || canDelete ? { right: ['actions'] } : undefined}
        exportFilename="voice-webhooks.csv"
        pageSize={10}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            Webhook{' '}
            <span className="font-medium text-foreground">{pendingDelete?.webhookName}</span> will
            be permanently deleted. Events firing this webhook will stop being delivered.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={confirmDelete}
      />

      <JsonViewerDialog
        open={jsonModal !== null}
        onOpenChange={(o) => !o && setJsonModal(null)}
        title={jsonModal?.title ?? ''}
        content={jsonModal?.content ?? ''}
      />
    </FillPage>
  );
}
